import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { formatDateTime } from "@/lib/status";

/**
 * Per-item note thread — visible to owner + purchasing + admin (not warehouse).
 * Props: open, onOpenChange, request, itemIndex, onSent?
 */
export default function LineNotesDialog({ open, onOpenChange, request, itemIndex, onSent }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const item = (request?.items || [])[itemIndex] || {};
  const notes = item.line_notes || [];

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/requests/${request.id}/items/${itemIndex}/notes`, { text: body });
      setText("");
      onSent?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gönderilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="line-notes-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-5 w-5 text-slate-600" /> Ürün Notları
          </DialogTitle>
          <DialogDescription className="text-xs">
            <span className="font-semibold text-slate-700">{item.product_code}</span> — {item.product_name}. Talep eden ile satınalmacı arasında bu ürüne özel notlar (depo görmez).
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-64 overflow-y-auto thin-scroll space-y-2 pr-1">
          {notes.length === 0 ? (
            <div data-testid="line-notes-empty" className="text-center text-sm text-slate-400 py-6">Henüz not yok.</div>
          ) : notes.map((n) => {
            const mine = n.user_id === user?.id;
            return (
              <div key={n.id} data-testid={`line-note-${n.id}`} className={`rounded-lg px-3 py-2 text-sm ${mine ? "bg-slate-900 text-white ml-6" : "bg-slate-100 text-slate-800 mr-6"}`}>
                <div className="whitespace-pre-wrap break-words">{n.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-slate-500"}`}>{n.user_name} • {formatDateTime(n.created_at)}</div>
              </div>
            );
          })}
        </div>

        <div className="flex items-end gap-2 pt-2 border-t border-slate-100">
          <textarea
            data-testid="line-note-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            placeholder="Not yazın... (Enter ile gönder)"
            className="flex-1 resize-none border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
          />
          <Button data-testid="line-note-send" onClick={send} disabled={busy || !text.trim()} className="h-10 w-10 p-0 shrink-0 bg-indigo-600 hover:bg-indigo-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
